import { useQuery } from "@apollo/client";
import { useState, useEffect } from "react";

import { generatePMEQueryWhereCond } from "#/src/utils/filter/assetsQueryCond";
import {
  LIST_PREVENTIVE_MAINTENANCE_EVENTS,
  LIST_ALL_PREVENTIVE_MAINTENANCE_EVENTS,
  LIST_SHARED_PREVENTIVE_MAINTENANCE_EVENTS,
} from "~/api";
import { useSearch } from "~/hooks";

/**
 *
 * @param query should contain an object with at least a key "where" and it's also an object with some specifice keys
 * See more details on this link: https://www.npmjs.com/package/mongo-search-parameters
 * @returns [PreventiveMaintenance] | []
 */
export const listAllPreventiveMaintenanceEvents = (
  query,
  skipCondition = false,
) => {
  const {
    data: { listPreventiveMaintenanceEvents } = {},
    loading,
    refetch,
  } = useQuery(LIST_PREVENTIVE_MAINTENANCE_EVENTS, {
    variables: {
      params: query,
    },
    skip: skipCondition,
    fetchPolicy: "cache-and-network",
  });

  return {
    loading,
    events: listPreventiveMaintenanceEvents,
    refetchEvents: () => refetch(),
  };
};

export const useListSharedPreventiveMaintenanceEvents = (
  query,
  skipCondition = false,
) => {
  const { data: { listSharedPreventiveMaintenanceEvents } = {}, loading } =
    useQuery(LIST_SHARED_PREVENTIVE_MAINTENANCE_EVENTS, {
      variables: {
        params: query,
      },
      skip: skipCondition,
    });

  return {
    loading,
    events: listSharedPreventiveMaintenanceEvents,
  };
};

/**
 * Custom hook for fetching preventive maintenance events with FE-based pagination.
 * @param {string} oemId - ID of the OEM (required to fetch relevant events).
 * @param {number} pageSize - Number of events to fetch per page (default: 100).
 * @returns {object} - { events, loading, fetchNextPage, hasMore }
 */
export const usePreventiveMaintenanceEvents = (
  oemId,
  activeFilters = {},
  pageSize = 100,
  futureOnly = false,
  sort = [],
) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allEvents, setAllEvents] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const { searchValue, handleSearch, updateSearchTerm } = useSearch();
  const [totalCount, setTotalCount] = useState(1);
  const filterConditions = generatePMEQueryWhereCond(activeFilters);
  const hasFilter = Object.keys(filterConditions)?.length;
  const whereBase = {
    searchQuery: searchValue || "",
    ...(hasFilter ? filterConditions : {}),
    ...(futureOnly ? { futureOnly: true } : {}),
  };
  const sortParam = Array.isArray(sort) && sort.length > 0 ? sort : undefined;
  const queryParams = {
    where: whereBase,
    limit: pageSize,
    skip: 0,
    ...(sortParam ? { sort: sortParam } : {}),
  };

  const { loading, fetchMore, refetch } = useQuery(
    LIST_ALL_PREVENTIVE_MAINTENANCE_EVENTS,
    {
      variables: {
        params: queryParams,
      },
      skip: !oemId,
      fetchPolicy: "cache-and-network",
      onCompleted: (data) => {
        const fetchedEvents =
          data?.listAllPreventiveMaintenanceEvents?.events || [];
        const totalCount =
          data?.listAllPreventiveMaintenanceEvents?.totalCount || 0;
        setCurrentPage(1);
        setAllEvents(fetchedEvents);
        setTotalCount(totalCount);
        setHasMore(fetchedEvents.length === pageSize);
      },
    },
  );

  const fetchNextPage = () => {
    if (loading || !hasMore) return;

    fetchMore({
      variables: {
        params: {
          ...queryParams,
          skip: currentPage * pageSize,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const newEvents =
          fetchMoreResult?.listAllPreventiveMaintenanceEvents?.events || [];
        if (newEvents.length < pageSize) setHasMore(false);
        setCurrentPage((prevPage) => prevPage + 1);
        setAllEvents((prev) => [...prev, ...newEvents]);
      },
    });
  };

  const refetchEvents = async () => {
    setCurrentPage(1);
    setHasMore(true);
    // Don't clear allEvents first — avoids an empty flash / stuck empty if
    // refetch races with Apollo's variables-driven request.
    const result = await refetch({ params: queryParams });

    const fetchedEvents =
      result?.data?.listAllPreventiveMaintenanceEvents?.events || [];
    const total =
      result?.data?.listAllPreventiveMaintenanceEvents?.totalCount || 0;
    setAllEvents(fetchedEvents);
    setTotalCount(total);
    setHasMore(fetchedEvents.length === pageSize);
    return result;
  };

  // Apollo already refetches when `variables` (search/filters/sort) change.
  // Only reset pagination bookkeeping — do not wipe events or double-fetch.
  const sortKey = sortParam?.join(",") ?? "";
  const filterKey = JSON.stringify(filterConditions);
  useEffect(() => {
    setCurrentPage(1);
    setHasMore(true);
  }, [searchValue, oemId, pageSize, futureOnly, sortKey, filterKey]);

  return {
    events: allEvents,
    loading,
    fetchNextPage,
    hasMore,
    handleSearch,
    searchValue,
    updateSearchTerm,
    refetchEvents,
    totalCount,
  };
};
